import React, { useState } from 'react';

import {
	Dialog,
	DialogContent,
	TextField,
	FormControlLabel,
	Checkbox,
	FormLabel,
	FormGroup,
	Box,
	Collapse,
	Button,
	Typography,
	Stepper,
	Step,
	StepLabel,
	StepContent,
	CircularProgress,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { useSelector } from 'react-redux';

import CopyableText from 'components/CopyableText';
import { Scopes } from 'common/constants/api';
import { createApiKey, KeyDetails } from 'common/api/frontend/apiKeys';

import * as ShopSelectors from 'selectors/ShopSelectors';
import { useTranslation } from 'services/localization';
import Banner from 'components/Banner';

interface Props {
	isOpen: boolean;
	onClose: () => void;
}

interface FormState {
	scopes: Scopes[];
	label: string;
}
const INITIAL_FORM_STATE = {
	scopes: [],
	label: '',
};

const CreateApiKeyModal = (props: Props) => {
	const classes = useStyles();
	const { t } = useTranslation();

	const [formState, setFormState] = useState<FormState>(INITIAL_FORM_STATE);
	const [error, setError] = useState<string | null>(null);
	const [submitState, setSubmitState] = useState<'initial' | 'loading' | 'done'>('initial');
	const [key, setKey] = useState<KeyDetails | null>(null);
	const [activeStep, setActiveStep] = useState<number>(0);

	const shopId = useSelector(ShopSelectors.activeShopId)!;

	const resetDialog = () => {
		setSubmitState('initial');
		setFormState(INITIAL_FORM_STATE);
		setActiveStep(0);
		setError(null);
		setKey(null);
	};

	const handleScopeToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
		const scope = event.target.name as Scopes;
		if (formState.scopes.includes(scope)) {
			setFormState({
				...formState,
				scopes: formState.scopes.filter((s) => s !== scope),
			});
		} else {
			setFormState({
				...formState,
				scopes: [...formState.scopes, scope],
			});
		}
	};

	const handleLabelChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setFormState({
			...formState,
			label: event.target.value,
		});
	};

	const handleSubmit = async () => {
		setSubmitState('loading');

		try {
			const secret = await createApiKey(formState.label, shopId, formState.scopes);
			setKey(secret);
			setSubmitState('done');
		} catch (err) {
			setError(
				t('Integrations.CreateApiKeyModal.error', 'Error creating API key. Please try again.'),
			);
			setSubmitState('initial');
		}
	};

	const renderScopeGroup = (groupName: string, scopes: Scopes[]) => {
		return (
			<Box mt={2} ml={2}>
				<Box display="flex" flexDirection="row">
					<Box mt={1.5} mb={1}>
						<FormLabel component="legend" className={classes.scopesGroupTitle}>
							<Typography variant="body2">{groupName}</Typography>
						</FormLabel>
					</Box>
					<FormGroup className={classes.scopesGroupContent}>
						{scopes.map(renderScopeCheckbox)}
					</FormGroup>
				</Box>
			</Box>
		);
	};

	const renderScopeCheckbox = (scope: Scopes) => {
		return (
			<FormControlLabel
				key={scope}
				label={scope}
				control={
					<Checkbox
						color="primary"
						checked={formState.scopes.includes(scope)}
						onChange={handleScopeToggle}
						name={scope}
					/>
				}
			/>
		);
	};

	const renderStepButtons = (
		currentStep: number,
		options: { nextDisabled: boolean; isLastStep?: boolean },
	) => {
		return (
			<Box display="flex" flexDirection="row">
				{currentStep !== 0 && (
					<Box mr={2}>
						<Button
							color="secondary"
							variant="outlined"
							onClick={() => setActiveStep(currentStep - 1)}
						>
							{t('common:actions.back')}
						</Button>
					</Box>
				)}
				{!options.isLastStep ? (
					<Button
						variant="contained"
						color="primary"
						disabled={options.nextDisabled}
						onClick={() => setActiveStep(currentStep + 1)}
					>
						{t('common:actions.next', 'Next')}
					</Button>
				) : (
					<Button
						variant="contained"
						color="primary"
						disabled={options.nextDisabled}
						onClick={handleSubmit}
					>
						{t('Integrations.CreateApiKeyModal.createApiKey', 'Create API key')}
					</Button>
				)}
			</Box>
		);
	};

	return (
		<Dialog
			open={props.isOpen}
			onClose={props.onClose}
			aria-labelledby="apikey-create-modal-title"
			fullWidth
			maxWidth="md"
			disableEscapeKeyDown={submitState !== 'initial'}
			TransitionProps={{
				onExited: resetDialog,
			}}
		>
			<DialogContent>
				<Collapse in={submitState === 'initial'}>
					<Typography variant="h5" id="apikey-create-modal-title" gutterBottom>
						{t('Integrations.CreateApiKeyModal.createApiKey', 'Create API key')}
					</Typography>
					{error && <Banner title={error} variant="danger" />}
					<Stepper activeStep={activeStep} orientation="vertical">
						<Step>
							<StepLabel>
								{t('Integrations.CreateApiKeyModal.selectScopes', 'Select scopes')}
							</StepLabel>
							<StepContent>
								<Typography variant="body2" color="textSecondary">
									{t(
										'Integrations.CreateApiKeyModal.selectScopesDescription',
										'Refer to the Rentle API documentation to see which scopes you need to enable for the operations you wish to perform via the API.',
									)}
								</Typography>
								<Box my={3}>
									{renderScopeGroup('Products', [
										Scopes.CREATE_PRODUCTS,
										Scopes.READ_PRODUCTS,
										Scopes.UPDATE_PRODUCTS,
										Scopes.DELETE_PRODUCTS,
									])}
									{renderScopeGroup('Shops', [Scopes.READ_SHOPS, Scopes.UPDATE_SHOPS])}
									{renderScopeGroup('Orders', [
										Scopes.CREATE_ORDERS,
										Scopes.READ_ORDERS,
										Scopes.UPDATE_ORDERS,
										Scopes.DELETE_ORDERS,
									])}
								</Box>
								{renderStepButtons(0, { nextDisabled: formState.scopes.length === 0 })}
							</StepContent>
						</Step>
						<Step>
							<StepLabel>
								{t('Integrations.CreateApiKeyModal.apiKeyLabel', 'API key label')}
							</StepLabel>
							<StepContent>
								<Typography variant="body2" color="textSecondary">
									{t(
										'Integrations.CreateApiKeyModal.apiKeyLabelDescription',
										'Give your API key a human-readable label',
									)}
								</Typography>
								<Box my={3}>
									<TextField
										label={t('Integrations.CreateApiKeyModal.enterLabel', 'Enter label')}
										fullWidth
										variant="outlined"
										value={formState.label}
										onChange={handleLabelChange}
									></TextField>
								</Box>
								{renderStepButtons(1, {
									nextDisabled: !formState.label,
									isLastStep: true,
								})}
							</StepContent>
						</Step>
					</Stepper>
				</Collapse>
				<Collapse in={submitState === 'loading'}>
					<Box
						p={3}
						minHeight={300}
						display="flex"
						flexDirection="column"
						alignItems="center"
						justifyContent="center"
					>
						<CircularProgress size={20} />
						<Box mt={2} />
						<Typography variant="h5" gutterBottom>
							{t('Integrations.CreateApiKeyModal.creatingApiKey', 'Creating your API key...')}
						</Typography>
					</Box>
				</Collapse>
				<Collapse in={submitState === 'done'}>
					<Typography variant="h5" id="apikey-create-modal-title" gutterBottom>
						{t('Integrations.CreateApiKeyModal.apiKeyCreated', 'API key created')}
					</Typography>
					<Banner
						variant="warning"
						title={t('Integrations.CreateApiKeyModal.storeCredentials', 'Store your credentials')}
						description={t(
							'Integrations.CreateApiKeyModal.storeCredentialsDescription',
							'This is the last time you will be able to see the credentials for this API key. Make sure to store them somewhere before closing this dialog.',
						)}
						mb={2}
					/>
					<Typography variant="body1" gutterBottom>
						{t('Integrations.CreateApiKeyModal.apiKeyId', 'API key ID')}:
					</Typography>
					<CopyableText text={key?.keyId ?? ''} />
					<Box mt={2} />
					<Typography variant="body1" gutterBottom>
						{t('Integrations.CreateApiKeyModal.apiKeySecret', 'API key secret')}:
					</Typography>
					<CopyableText text={key?.secret ?? ''} />
					<Box my={3} display="flex" flexDirection="row" justifyContent="flex-end">
						<Button color="primary" variant="contained" onClick={props.onClose}>
							{t('common:actions.done')}
						</Button>
					</Box>
				</Collapse>
			</DialogContent>
		</Dialog>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		scopesGroupTitle: {
			width: '100px',
		},
		scopesGroupContent: {
			display: 'flex',
			flexDirection: 'row',
		},
	}),
);

export default CreateApiKeyModal;
